import * as React from 'react';
import { cn } from '../utils/cn';
import { X } from 'lucide-react';
import { useFocusTrap } from './useFocusTrap';

export interface DrawerProps {
  /** Whether drawer is visible */
  visible?: boolean;
  /** Drawer title */
  title?: React.ReactNode;
  /** Position: bottom, right, left, top */
  placement?: 'bottom' | 'right' | 'left' | 'top';
  /** Show close button */
  closable?: boolean;
  /** Close callback */
  onClose?: () => void;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  /** Drawer height (for top/bottom placement) */
  height?: number | string;
  /** Drawer width (for left/right placement) */
  width?: number | string;
  children?: React.ReactNode;
}

const placementStyles: Record<string, string> = {
  bottom: 'inset-x-0 bottom-0 rounded-t-lg',
  top: 'inset-x-0 top-0 rounded-b-lg',
  right: 'inset-y-0 right-0 rounded-l-lg',
  left: 'inset-y-0 left-0 rounded-r-lg',
};

const placementTransform: Record<string, { open: string; closed: string }> = {
  bottom: { open: 'translate-y-0', closed: 'translate-y-full' },
  top: { open: 'translate-y-0', closed: '-translate-y-full' },
  right: { open: 'translate-x-0', closed: 'translate-x-full' },
  left: { open: 'translate-x-0', closed: '-translate-x-full' },
};

export function Drawer({
  visible = false,
  title,
  placement = 'bottom',
  closable = true,
  onClose,
  className,
  style,
  height,
  width,
  children,
}: DrawerProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Trap focus inside drawer when open
  useFocusTrap(panelRef, visible);

  // Lock body scroll
  React.useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [visible]);

  // Escape key
  React.useEffect(() => {
    if (!visible) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, onClose]);

  const isVertical = placement === 'bottom' || placement === 'top';
  const transform = placementTransform[placement];

  const sizeStyle: React.CSSProperties = {};
  if (isVertical) {
    sizeStyle.maxHeight = '85vh';
    if (height) sizeStyle.height = height;
  } else {
    sizeStyle.maxWidth = '85vw';
    if (width) sizeStyle.width = width;
    else sizeStyle.width = 320;
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-[1000] transition-opacity duration-normal ease-smooth',
        visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      )}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        className={cn(
          'fixed bg-white border border-border shadow-lg overflow-y-auto font-body',
          'transition-transform duration-normal ease-smooth',
          placementStyles[placement],
          visible ? transform.open : transform.closed,
          className
        )}
        style={{ ...sizeStyle, ...style }}
      >
        {/* Header */}
        {(title || closable) && (
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            {title && (
              <h3 className="text-lg font-body font-medium text-foreground">{title}</h3>
            )}
            {closable && (
              <button
                onClick={onClose}
                className="p-1! h-auto! border-0! bg-transparent! tracking-normal! normal-case! text-muted hover:text-foreground transition-colors duration-fast ml-auto"
                aria-label="Stäng"
              >
                <X size={18} />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="px-4 pb-4">
          {children}
        </div>
      </div>
    </div>
  );
}
